import { supabase } from '@/integrations/supabase/client';
import { sendTelegramNotification } from '@/utils/telegramNotifications';
import { formatDateTime } from '@/utils/telegramMessageFormat';
import { format } from 'date-fns';

interface BookingMatchContext {
  matchId: string;
  groupId?: string | null;
  playerIds: string[]; // all 4 player slots (may contain duplicates/nulls)
  currentUserId: string;
  matchDate?: string;  // ISO date string
  /** Optional IANA timezone for the match (e.g. "Europe/Madrid"). When provided, the
   *  formatted notification body includes the wall-clock time in that zone. */
  timezone?: string | null;
  clubName?: string;   // Club name for context
  rosterLabel?: string; // e.g. "Abhishek & Rahul vs Pedro & Alex"
}

function getOtherPlayerIds(ctx: BookingMatchContext): string[] {
  return [...new Set(
    ctx.playerIds.filter((id): id is string => !!id && id !== ctx.currentUserId)
  )];
}

function formatMatchDetails(ctx: BookingMatchContext): { dateStr: string; clubStr: string; rosterStr: string } {
  let dateStr = '';
  if (ctx.matchDate) {
    try {
      // Prefer the timezone-aware "Sat, 25 Apr · 18:00 IST" format when we have a tz.
      // Otherwise fall back to a date-only label so existing call-sites without a tz keep working.
      dateStr = ctx.timezone
        ? ` on ${formatDateTime(ctx.matchDate, ctx.timezone)}`
        : ` on ${format(new Date(ctx.matchDate), 'MMM d')}`;
    } catch { /* ignore parse errors */ }
  }
  const clubStr = ctx.clubName ? ` at ${ctx.clubName}` : '';
  const rosterStr = ctx.rosterLabel ? `\nMatch: ${ctx.rosterLabel}` : '';
  return { dateStr, clubStr, rosterStr };
}

/**
 * Build a compact roster label from player profile names.
 * Returns e.g. "Abhishek & Rahul vs Pedro & Alex" or "Abhishek vs Pedro".
 */
export function buildRosterLabel(
  t1p1?: string, t1p2?: string,
  t2p1?: string, t2p2?: string
): string {
  const firstName = (name?: string) => (name || 'TBD').split(' ')[0];
  const team1 = [t1p1, t1p2].filter(Boolean).map(firstName).join(' & ');
  const team2 = [t2p1, t2p2].filter(Boolean).map(firstName).join(' & ');
  if (!team1 && !team2) return '';
  if (!team2) return team1;
  if (!team1) return team2;
  return `${team1} vs ${team2}`;
}

async function sendPushToPlayers(playerIds: string[], title: string, body: string) {
  for (const id of playerIds) {
    supabase.functions.invoke('send-web-push', {
      body: { toUserId: id, title, body, url: '/', icon: '/pwa-192x192.png' },
    }).catch(() => {});
  }
}

async function insertNotifications(playerIds: string[], title: string, message: string, matchId: string) {
  if (playerIds.length === 0) return;
  await supabase.from('notifications').insert(
    playerIds.map(id => ({
      user_id: id,
      type: 'booking_update',
      title,
      message,
      content: { match_id: matchId } as any,
    }))
  ).then(({ error }) => {
    if (error) console.error('Failed to insert booking notifications:', error);
  });
}

/**
 * Fire-and-forget: notifies other match players that a booking was initiated.
 */
export function notifyBookingInitiated(ctx: BookingMatchContext, userName: string) {
  const others = getOtherPlayerIds(ctx);
  if (others.length === 0) return;

  const { dateStr, clubStr, rosterStr } = formatMatchDetails(ctx);
  const title = '🏟️ Court Booking';
  const body = `${userName} is booking a court${clubStr} for your match${dateStr}${rosterStr}`;

  sendPushToPlayers(others, title, body);
  insertNotifications(others, title, body, ctx.matchId);

  if (ctx.groupId) {
    sendTelegramNotification({
      groupId: ctx.groupId,
      title,
      message: body,
      type: 'booking_update',
    });
  }
}

/**
 * Fire-and-forget: notifies other match players that a court was booked.
 */
export function notifyBookingConfirmed(ctx: BookingMatchContext, userName: string, courtName?: string) {
  const others = getOtherPlayerIds(ctx);
  if (others.length === 0) return;

  const { dateStr, clubStr, rosterStr } = formatMatchDetails(ctx);
  const title = '✅ Court Booked';
  const courtInfo = courtName ? ` (${courtName})` : '';
  const body = `${userName} booked a court${courtInfo}${clubStr} for your match${dateStr}${rosterStr}`;

  sendPushToPlayers(others, title, body);
  insertNotifications(others, title, body, ctx.matchId);

  if (ctx.groupId) {
    sendTelegramNotification({
      groupId: ctx.groupId,
      title,
      message: body,
      type: 'booking_update',
    });
  }
}

/**
 * Fire-and-forget: notifies other match players that the booking was cancelled.
 */
export function notifyBookingCancelled(ctx: BookingMatchContext, userName: string) {
  const others = getOtherPlayerIds(ctx);
  if (others.length === 0) return;

  const { dateStr, clubStr, rosterStr } = formatMatchDetails(ctx);
  const title = '❌ Booking Cancelled';
  const body = `${userName} could not book a court${clubStr} for match${dateStr}. You can try booking now.${rosterStr}`;

  sendPushToPlayers(others, title, body);
  insertNotifications(others, title, body, ctx.matchId);

  if (ctx.groupId) {
    sendTelegramNotification({
      groupId: ctx.groupId,
      title,
      message: body,
      type: 'booking_update',
    });
  }
}
